import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Loader from "../../components/Loader";
import "./Reviews.css";
import { useTranslation } from "react-i18next";               // 🆕 i18n import
import { useAuth } from "../../contexts/AuthProvider";
import { API_URL } from "../../utils/config";
const baseUrl = API_URL;
const Reviews = () => {
  const { t } = useTranslation("reviews");                   // 🆕 namespace
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState({});
  const { isAuthenticated } = useAuth();

  const getLangFromLocalStorage = () => {
    return localStorage.getItem("language") || "en";
  };

  /* ---------------------------- API --------------------------- */
  const fetchReviews = async () => {
    try {
      if (!isAuthenticated) {
        localStorage.removeItem("user");
        navigate("/login");
        return [];
      }
      const response = await fetch(`${baseUrl}/api/v1/attnd-supplier/mySuppliers/reviews`, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          lang: getLangFromLocalStorage(),

        },
      });

      if (response.status === 401) {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        navigate("/login");
        return;
      }

      if (!response.ok) throw new Error("Failed to fetch reviews");

      return await response.json();
    } catch (error) {
      console.error("Error fetching reviews:", error);
      return [];
    }
  };

  useEffect(() => {
    const getReviews = async () => {
      setLoading(true);
      const data = await fetchReviews();
      setReviews(data || {});
      setLoading(false);
    };
    getReviews();
  }, []);

  /* ---------------------------- UI ---------------------------- */
  if (loading) {
    return (
      <div className="loader-container">
        <Loader />
      </div>
    );
  }

  return (
    <div className="reviews-main-content">
      {/* ---------- header & stats ---------- */}
      <div className="reviews-header">
        <h2 className="reviews-heading">{t("header.title")}</h2>
        <p className="reviews-desc">{t("header.subtitle")}</p>

        <div className="reviews-stats">
          {/* total reviews */}
          <div className="total-reviews">
            <h4>{t("stats.totalTitle")}</h4>
            <span>{reviews?.data?.totalReviews || 0}</span>
            <p>{t("stats.totalDesc")}</p>
          </div>

          {/* average rating */}
          <div className="avg-review">
            <h4>{t("stats.avgTitle")}</h4>
            <div className="avg-rating">
              <span className="avg-number">
                {reviews?.data?.averageRating || 0}
              </span>
              <div className="rating-icons">
                {Array.from(
                  { length: reviews?.data?.averageRating },
                  (_, i) => i + 1
                ).map((_, idx) => (
                  <img key={idx} src="/star.svg" alt={t("breakdown.starAlt")} />
                ))}
              </div>
            </div>
            <p>{t("stats.avgDesc")}</p>
          </div>

          {/* rating breakdown */}
          <div className="review-rating">
            <div style={{ width: "185px" }}>
              {["5", "4", "3", "2", "1"].map((num, idx) => (
                <div
                  key={idx}
                  className={`${["five", "four", "three", "two", "one"][idx]}-stars`}
                >
                  <img
                    className="empty-rating-icon"
                    src="/Star 1.svg"
                    alt={t("breakdown.starAlt")}
                  />
                  <span>{num}</span>
                  <div
                    className={`${["five", "four", "three", "two", "one"][idx]}-star-bar`}
                  ></div>
                  <span>{reviews?.data?.ratingBreakdown?.[num] || 0}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ---------- individual reviews ---------- */}
      {reviews?.reviews?.length > 0 && (
        <div className="reviewers-content">
          <h3 className="all-reviews">{t("sections.all")}</h3>

          <div className="reviewers">
            {reviews.reviews.map((review, index) => (
              <div className="reviewer-card" key={index}>
                {/* reviewer info */}
                <div className="reviewer-info">
                  {review?.user?.image ? (
                    <img src={review.user.image} alt="reviewer" />
                  ) : (
                    <img src="/reviewer.svg" alt="reviewer" />
                  )}
                  <p>{review?.user?.fullName}</p>
                </div>

                {/* rating & message */}
                <div className="reviewer-rate">
                  <div className="reviewer-rate-content">
                    <div className="rate-icons">
                      {Array.from({ length: review?.rating }, (_, i) => i + 1).map(
                        (_, idx) => (
                          <img
                            key={idx}
                            src="/star.svg"
                            alt={t("breakdown.starAlt")}
                          />
                        )
                      )}
                    </div>
                    <p className="rate-date">
                      {review?.created_at
                        ? (() => {
                            const d = new Date(review.created_at);
                            const day = String(d.getDate()).padStart(2, "0");
                            const month = String(d.getMonth() + 1).padStart(2, "0");
                            const year = d.getFullYear();
                            return `${day}-${month}-${year}`;
                          })()
                        : t("sections.noDate")}
                    </p>
                  </div>
                  <p className="rate-comment">{review?.message}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Reviews;
